import { useEffect, useState, useCallback } from 'react';
import './RivalActivityToast.css';
import { GameState, generateActivities } from '../helper/ActivityGenerator';

export interface RivalActivityItem {
  id: string;
  date: string;
  companyName: string;
  gameName: string;
  activity: string;
  colour: string;
  displayed: boolean;
}

interface RivalActivityToastProps {
  gameName: string;
  companyName: string;
  companyColour: string;
  gameStartTime: string | null;
  enabled: boolean;
  gameState: GameState;
}

/**
 * Calculate the relative luminance of a colour to determine if text should be light or dark.
 * Uses the WCAG formula for luminance calculation.
 * Returns a colour that provides good contrast against the background.
 */
export function getContrastingTextColour(hexColour: string): string {
  // Remove # if present
  let hex = hexColour.replace('#', '');
  
  // Validate and normalize hex string
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    // Invalid hex, return white as default (safe for most backgrounds)
    return '#ffffff';
  }
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Apply sRGB to linear RGB conversion
  const linearR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const linearG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const linearB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance
  const luminance = 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
  
  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

export default function RivalActivityToast({ gameName, companyName, companyColour, gameStartTime, enabled, gameState }: RivalActivityToastProps) {
  const [activities, setActivities] = useState<RivalActivityItem[]>([]);
  const [currentToast, setCurrentToast] = useState<RivalActivityItem | null>(null);
  const [lastDisplayTime, setLastDisplayTime] = useState<number>(Date.now());

  // Fetch activities from the API
  const fetchActivities = useCallback(async () => {
    if (!enabled || !gameName || !companyName || !gameStartTime) return;

    try {
      const params = new URLSearchParams({
        gameName,
        companyName,
        startTime: gameStartTime,
      });
      const response = await fetch(`/api/get-activities?${params}`);
      const data = await response.json();
      
      if (response.ok && data.activities) {
        setActivities((prevActivities) => {
          // Get existing IDs to avoid duplicates
          const existingIds = new Set(prevActivities.map(a => a.id));
          
          // Add new activities with displayed: false
          const newActivities = data.activities
            .filter((a: RivalActivityItem) => !existingIds.has(a.id))
            .map((a: RivalActivityItem) => ({ ...a, displayed: false }));
          
          return [...prevActivities, ...newActivities];
        });
      }
    } catch (error) {
      console.error('Error fetching rival activities:', error);
    }
  }, [enabled, gameName, companyName, gameStartTime]);

  // Save activity to the API
  const saveActivity = useCallback(async () => {
    if (!enabled || !gameName || !companyName) return;

    try {
      // Generate dynamic activities based on game state
      const generatedActivities = generateActivities(gameState);
      
      // Save each generated activity
      for (const activity of generatedActivities) {
        await fetch('/api/save-activity', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            gameName,
            companyName,
            activity,
            colour: companyColour,
          }),
        });
      }
    } catch (error) {
      console.error('Error saving activity:', error);
    }
  }, [enabled, gameName, companyName, companyColour, gameState]);

  // Trigger both API calls when enabled changes (sprint results shown)
  useEffect(() => {
    if (enabled && gameStartTime) {
      fetchActivities();
      saveActivity();
    }
  }, [enabled, fetchActivities, saveActivity, gameStartTime]);

  // Display logic for toasts - 10% random chance or 60-second fallback
  useEffect(() => {
    if (!enabled || currentToast) return;

    const undisplayedActivities = activities.filter(a => !a.displayed);
    if (undisplayedActivities.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastDisplay = (now - lastDisplayTime) / 1000;

      // 10% random chance or 60 seconds have passed since last display
      const shouldDisplay = Math.random() < 0.1 || timeSinceLastDisplay >= 60;

      if (shouldDisplay) {
        // Get next undisplayed activity from filtered list
        const nextActivity = undisplayedActivities.find(a => !a.displayed);
        if (nextActivity) {
          setCurrentToast(nextActivity);
          setLastDisplayTime(now);
          setActivities(prev => 
            prev.map(a => 
              a.id === nextActivity.id ? { ...a, displayed: true } : a
            )
          );
        }
      }
    }, 5000); // Check every 5 seconds for better efficiency

    return () => clearInterval(interval);
  }, [enabled, activities, currentToast, lastDisplayTime]);

  // Auto-hide toast after 30 seconds (unless a new message is displayed)
  useEffect(() => {
    if (currentToast) {
      const timer = setTimeout(() => {
        setCurrentToast(null);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [currentToast]);

  if (!currentToast) return null;

  // Calculate text colour based on toast background colour for readability
  const toastBgColour = currentToast.colour || '#808080';
  const textColour = getContrastingTextColour(toastBgColour);

  return (
    <div className="rival-activity-toast">
      <div 
        className="toast-content"
        style={{
          backgroundColor: toastBgColour,
          color: textColour,
          borderColor: textColour,
        }}
      >
        <span className="toast-icon">📢</span>
        <span className="toast-message">
          <strong style={{ color: textColour }}>{currentToast.companyName}</strong>: {currentToast.activity}
        </span>
      </div>
    </div>
  );
}
